// components/SourceList.tsx — lists ingested sources (docs/notes/git) with delete + optional edit.
import { useEffect, useState } from 'react';
import { useServices } from '../app/AppContext';
import type { Source } from '../lib/api';
import { ConfirmDialog } from './Dialog';

function formatWhen(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? ''
    : d.toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' });
}

export interface SourceListProps {
  kind: string;
  title: string;
  emptyText: string;
  /** Bump to refetch after an upload/connect/edit. */
  reloadKey?: number;
  /** Optional secondary line per row (e.g. a git repo URL). */
  describe?: (s: Source) => string | null;
  /** When provided, each row shows an Edit button. */
  onEdit?: (s: Source) => void;
  confirmMessage: (s: Source) => string;
}

export function SourceList({
  kind,
  title,
  emptyText,
  reloadKey = 0,
  describe,
  onEdit,
  confirmMessage,
}: SourceListProps) {
  const { api } = useServices();
  const [sources, setSources] = useState<Source[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState<Source | null>(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    api
      .listSources(kind)
      .then((r) => {
        if (active) {
          setSources(r.sources);
          setError(null);
        }
      })
      .catch((e) => {
        if (active) setError(e instanceof Error ? e.message : 'Failed to load');
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [api, kind, reloadKey]);

  async function confirmDelete(target: Source) {
    setDeleting(true);
    try {
      await api.deleteSource(target.id);
      setSources((list) => list.filter((s) => s.id !== target.id));
      setPending(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to delete');
    } finally {
      setDeleting(false);
    }
  }

  return (
    <section className="card">
      <h2 className="text-base font-semibold">{title}</h2>
      {error ? (
        <p role="alert" className="mt-2 text-sm text-danger">
          {error}
        </p>
      ) : null}
      {loading ? (
        <p className="mt-3 text-sm text-fg-muted">Loading…</p>
      ) : sources.length === 0 ? (
        <p className="mt-3 text-sm text-fg-muted">{emptyText}</p>
      ) : (
        <ul className="mt-3 divide-y divide-line/10" data-testid={`source-list-${kind}`}>
          {sources.map((s) => {
            const secondary = describe ? describe(s) : null;
            const label = s.label ?? '(untitled)';
            const when = formatWhen(s.createdAt);
            return (
              <li key={s.id} className="flex items-center justify-between gap-3 py-3">
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-fg">{label}</p>
                  {secondary ? <p className="truncate text-xs text-fg-muted">{secondary}</p> : null}
                  {when ? <p className="text-xs text-fg-subtle">{when}</p> : null}
                </div>
                <div className="flex shrink-0 items-center gap-1.5">
                  {onEdit ? (
                    <button
                      type="button"
                      className="btn-ghost text-sm"
                      aria-label={`Edit ${label}`}
                      onClick={() => onEdit(s)}
                    >
                      Edit
                    </button>
                  ) : null}
                  <button
                    type="button"
                    className="btn-ghost text-sm text-danger"
                    aria-label={`Delete ${label}`}
                    onClick={() => setPending(s)}
                  >
                    Delete
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      <ConfirmDialog
        open={pending !== null}
        title="Delete this item?"
        message={pending ? confirmMessage(pending) : ''}
        confirmLabel="Delete"
        danger
        busy={deleting}
        onCancel={() => setPending(null)}
        onConfirm={() => {
          if (pending) void confirmDelete(pending);
        }}
      />
    </section>
  );
}
